import { useControlFiltersSync } from "@/features/controls/hooks/use-control-filters-sync";
import type { ControlListItem } from "@/models/control.model";
import { DEFAULT_PAGE_SIZE } from "@/shared/constants/pagination";
import { useControlRepository } from "@/shared/contexts/repository-context";
import { getGetControlListQueryKey } from "@/spz-api/controls/controls";
import type { GetControlListParams } from "@/spz-api/model";
import { useQuery } from "@tanstack/react-query";
import {
  type Row,
  type RowSelectionState,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useControlListColumns } from "../hooks/use-control-list-columns";

const useControlListRows = (params: GetControlListParams) => {
  const controlRepository = useControlRepository();

  const controlsAndTags = useQuery({
    queryKey: getGetControlListQueryKey(params),
    queryFn: () => controlRepository.getControlsAndTags(params),
    enabled: Boolean(params.analysisId),
  });

  return {
    data: controlsAndTags.data?.controls ?? [],
    currency: controlsAndTags.data?.currency,
    isLoading: controlsAndTags.isLoading,
    uniqueTags: controlsAndTags.data?.tags ?? [],
  };
};

export function useControlListDataTableViewModel({
  analysisId,
}: { analysisId: string }) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const { t } = useTranslation();

  const columns = useControlListColumns();
  const rows = useControlListRows({ analysisId });

  const { columnFilters, setColumnFilters, globalFilter, setGlobalFilter } =
    useControlFiltersSync();

  const globalFilterFn = (
    row: Row<ControlListItem>,
    _: string,
    filterValue: string,
  ) => {
    if (!filterValue) return true;
    const search = filterValue.toLowerCase().trim();
    const translatedControl = t(row.original.control);

    // So that filter matches what's displayed to the user
    const displayId = String(row.getValue("id"));
    return (
      displayId?.toLowerCase().includes(search) ||
      translatedControl?.toLowerCase().includes(search)
    );
  };

  const table = useReactTable({
    data: rows.data ?? [],
    columns,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (originalRow) => originalRow.id,
    initialState: {
      columnPinning: {
        left: ["select", "id"],
        right: ["actions"],
      },
      pagination: {
        pageSize: DEFAULT_PAGE_SIZE,
      },
    },
    state: {
      globalFilter,
      columnFilters,
      rowSelection,
    },
    enableMultiSort: false,
    autoResetPageIndex: false,
    globalFilterFn,
    onGlobalFilterChange: (value) => {
      setGlobalFilter(value);
      table.resetPageIndex();
    },
    onColumnFiltersChange: (value) => {
      setColumnFilters(value);
      table.resetPageIndex();
    },
    onRowSelectionChange: setRowSelection,
    meta: {
      getRowClassName: (row: Row<ControlListItem>) => {
        return Number(row.original.resultCount) === 0
          ? "border-l-2 border-l-green-500 bg-green-gradient *:bg-opacity-0"
          : "";
      },
    },
    enableColumnResizing: false,
  });

  // CLEAR CHOSEN STATES FROM TABLE WHEN switching analysis
  // Possible to do the same with filters if needed
  // biome-ignore lint/correctness/useExhaustiveDependencies: this is intentional to avoid passing rowSelection state between different analyses.
  useEffect(() => {
    setRowSelection({});
    table.resetPageIndex();
  }, [analysisId]);

  return {
    table,
    isLoading: rows.isLoading,
    uniqueTags: rows.uniqueTags,
    currency: rows.currency,
  };
}
